import { defineStore } from 'pinia'
import { useToast } from 'vuestic-ui'
import { API } from '../../../services'
import { defaultPagination, defaultSorting } from '../../../services/defaults'
import type { APIResponse, Filters, Pagination } from '../../../services/types'
import { setPagination } from '../../../services/utils/utils'
import { isSuccessStatus } from '../../../utils/http'
import type { UnitApi, UnitInput } from '../types'

const { init } = useToast()

export const useUnitsStore = defineStore('unitsStore', {
  state: () => ({
    loading: false,
    filters: {} as Filters,
    pagination: defaultPagination,
    sorting: defaultSorting,
    units: [] as UnitApi[],
    /** Cached catalog for dropdowns (e.g. item form). */
    selectOptions: [] as UnitApi[],
  }),
  actions: {
    initLoading() {
      this.loading = true
    },
    stopLoading() {
      this.loading = false
    },
    setUnits(units: UnitApi[]) {
      this.units = units
    },
    setSelectOptions(units: UnitApi[]) {
      this.selectOptions = units
    },
    setPagination(pagination: Pagination) {
      this.pagination = setPagination(pagination)
    },
    addUnit(unit: UnitApi) {
      this.units.unshift(unit)
      this.selectOptions = [...this.selectOptions, unit]
    },
    editUnit(unit: UnitApi) {
      const listIndex = this.units.findIndex((row) => row.uuid === unit.uuid)
      if (listIndex !== -1) {
        this.units[listIndex] = unit
      }
      const selectIndex = this.selectOptions.findIndex((row) => row.uuid === unit.uuid)
      if (selectIndex !== -1) {
        this.selectOptions[selectIndex] = unit
      }
    },
    removeUnit(unitUuid: string) {
      this.units = this.units.filter((row) => row.uuid !== unitUuid)
      this.selectOptions = this.selectOptions.filter((row) => row.uuid !== unitUuid)
    },
    async dispatchGetUnits(filters?: Filters): Promise<{ success: boolean }> {
      this.initLoading()
      const { status, data } = await API.units.getUnits(filters, this.pagination).finally(this.stopLoading)

      if (isSuccessStatus(status)) {
        this.setUnits(data.data)
        this.setPagination({ meta: data.meta })
      }

      return { success: isSuccessStatus(status) }
    },
    async dispatchGetUnitsForSelect(): Promise<{ success: boolean; data: UnitApi[] }> {
      const { status, data } = await API.units.getUnitsForSelect()

      if (isSuccessStatus(status)) {
        this.setSelectOptions(data.data)
      }

      return { success: isSuccessStatus(status), data: data?.data ?? [] }
    },
    async dispatchCreateUnit(input: UnitInput): Promise<APIResponse<null>> {
      this.initLoading()
      const { status, data } = await API.units.createUnit(input).finally(this.stopLoading)

      if (isSuccessStatus(status) && data.data) {
        this.addUnit(data.data)
        init({ message: 'Unit created successfully', color: 'success' })
      }

      return { success: isSuccessStatus(status) }
    },
    async dispatchUpdateUnit(input: UnitInput & Pick<UnitApi, 'uuid'>): Promise<APIResponse<null>> {
      this.initLoading()
      const { status, data } = await API.units.updateUnit(input).finally(this.stopLoading)

      if (isSuccessStatus(status) && data.data) {
        this.editUnit(data.data)
        init({ message: 'Unit updated successfully', color: 'success' })
      }

      return { success: isSuccessStatus(status) }
    },
    async dispatchDeactivateUnit(unit: UnitApi): Promise<APIResponse<null>> {
      this.initLoading()
      const { status } = await API.units.deactivateUnit(unit.uuid).finally(this.stopLoading)

      if (isSuccessStatus(status)) {
        this.removeUnit(unit.uuid)
        init({ message: 'Unit deactivated successfully', color: 'success' })
      }

      return { success: isSuccessStatus(status) }
    },
  },
})
